"use client";
import { useState, useRef, useEffect, type ReactNode } from "react";
import { cn } from "~/lib/utils";

interface Option {
  value: string;
  label: string;
}

interface CustomSelectProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  icon?: ReactNode; 
  className?: string;
}

export function CustomSelect({
  options,
  value,
  onChange,
  icon,
  className,
}: CustomSelectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);
  const displayLabel = selectedOption?.label ?? "انتخاب کنید";

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          "focus:border-primary flex flex-row items-center gap-3 rounded-2xl border-2 border-gray-600 px-3 py-2.5 w-50",
          "text-sm text-gray-400 bg-transparent",
          "transition-colors duration-200"
        )}
      >
        {icon}
        <span className="flex-1 text-right">{displayLabel}</span>
        <ChevronDownIcon open={open} />
      </button>

      {open && (
        <ul
          className={cn(
            "absolute z-21 top-full mt-2 w-full bg-gray-900 border border-gray-600 rounded-2xl shadow-lg overflow-hidden",
            "py-1"
          )}
        >
          {options.map((opt) => (
            <li
              key={opt.value}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={cn(
                "px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 cursor-pointer transition-colors",
                opt.value === value && "bg-gray-800 text-white"
              )}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ChevronDownIcon({ open }: { open: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#A4A4A4"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn("transition-transform duration-200", open && "rotate-180")}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}